"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Check, Loader2, Sparkles, Undo2, X } from "lucide-react";
import { AssistantVoiceTools } from "./assistant-voice-tools";
import { prepareEditorNavigation } from "@/lib/editor/navigation-guard";
import { useEditorStore, waitForPendingEditorSave } from "@/stores/editor-store";
import { useAssistantChatStore, MSG_CAP, MSG_SUMMARIZE_COUNT, type Msg, type ToolEvent, type CreatedPayload, type ProposalPayload, canApplyAssistantHydration, buildOrphanProposalMessage } from "@/stores/assistant-chat-store";
import { parseRecordCandidatePreviewPayload } from "@/lib/ai/assistant-v2/record-preview";
import { approveProposal, rejectProposal, undoAiCreation, summarizeConversationHistory, listConversations, getConversation } from "@/lib/actions/assistant-actions";
import { useEditorBackend } from "@/lib/editor-backend/context";
import { fireDemoEngagementOnce } from "@/lib/analytics/demo-engagement";
import { startAuthorToolTracking } from "@/lib/analytics/author-tools-client";
import { captureClientEvent } from "@/lib/analytics/client";
import { receiveAssistantIndexJobEvent } from "@/components/editor/assistant-index-event";
import { AssistantComposer, type AssistantComposerHandle } from "@/components/editor/assistant-composer";
import { AssistantSessionToolbar, type AssistantConversationSummary } from "@/components/editor/assistant-session-toolbar";
import type { AssistantExperience } from "@/lib/ai/assistant-v2/entitlement";
import { applyAssistantV2ClientEvent } from "@/lib/editor/assistant-v2-client-events";
import { ASSISTANT_V2_MAX_MESSAGE_CHARS } from "@/lib/ai/assistant-v2/limits";
import { parseAssistantResponseDeliveryMetadata, type AssistantFeedbackRating } from "@/lib/ai/assistant-v2/response-delivery";
import { AssistantResponseFeedbackControls } from "@/components/editor/assistant-response-feedback";
import { AssistantMessageContent } from "@/components/editor/assistant-message-content";
import { AssistantFlareControl } from "@/components/editor/assistant-flare-control";
interface AssistantSidebarProps {
  storyId: string;
  chapterId: string;
  assistantExperience?: AssistantExperience;
  assistantFlareAvailable?: boolean;
}
const UNDO_WINDOW_MS = 30_000;
const LEGACY_ACTION_UNAVAILABLE_MESSAGE = "Legacy action unavailable in Assistant Flare";
// The server stops a turn early (before Vercel's hard maxDuration kill) and
// emits a "deadline" warning when a tool loop runs long. We auto-continue by
// firing a follow-up turn — capped so a pathological loop can't run forever.
const AUTO_CONTINUE_MESSAGE = "Continue where you left off.";
const MAX_AUTO_CONTINUES = 3;
const SCAN_SAVED_CHAPTER_PROMPT = "Scan this saved chapter for new or changed Wiki and plot details, and show me a preview of any suggested record changes.";
const ASSISTANT_V2_SUGGESTIONS = ["Where might this chapter drag?", "Update my Wiki from this chapter", "Check whether this changes a plot thread"] as const;
interface LoadedConversationData {
  messages: Array<{
    id: string;
    role: string;
    content: unknown;
    deliveryMetadata?: unknown;
  }>;
  proposals: Array<{
    id: string;
    messageId: string | null;
    kind: string;
    display: string;
    payload: unknown;
    status: string;
    failureMessage: string | null;
  }>;
  recordCandidates?: Array<{
    candidateId: string;
    responseId: string;
    status: string;
    operations: Array<{
      position: number;
      kind: string;
      payload: unknown;
      verificationStatus: string;
      verificationIssues: unknown;
    }>;
  }>;
  feedback?: Array<{
    responseId: string;
    rating: string;
  }>;
}
function buildHydratedMessages(loaded: LoadedConversationData): Msg[] {
  const feedbackByResponse = new Map((loaded.feedback ?? []).filter((feedback): feedback is {
    responseId: string;
    rating: AssistantFeedbackRating;
  } => feedback.rating === "helpful" || feedback.rating === "not_helpful").map(feedback => [feedback.responseId, feedback.rating]));
  const proposalsByMessage = new Map<string, LoadedConversationData["proposals"]>();
  for (const proposal of loaded.proposals) {
    if (!proposal.messageId) continue;
    const list = proposalsByMessage.get(proposal.messageId) ?? [];
    list.push(proposal);
    proposalsByMessage.set(proposal.messageId, list);
  }
  const recordCandidatesByMessage = new Map((loaded.recordCandidates ?? []).map(candidate => [candidate.responseId, candidate]));
  const hydrated: Msg[] = loaded.messages.flatMap(message => {
    const content = Array.isArray(message.content) ? message.content as Array<Record<string, unknown>> : [];
    const text = content.filter(block => block.type === "text").map(block => String(block.text ?? "")).join("");
    const events: ToolEvent[] = (proposalsByMessage.get(message.id) ?? []).map(proposal => ({
      kind: "proposal" as const,
      payload: {
        proposalId: proposal.id,
        kind: proposal.kind as ProposalPayload["kind"],
        display: proposal.display,
        payload: proposal.payload
      },
      state: proposal.status === "applied" ? "approved" as const : proposal.status === "rejected" ? "rejected" as const : proposal.status === "failed" ? "error" as const : "pending" as const,
      message: proposal.status === "applied" ? "Approved" : proposal.status === "rejected" ? "Rejected" : proposal.failureMessage ?? undefined
    }));
    const recordCandidate = parseRecordCandidatePreviewPayload(recordCandidatesByMessage.get(message.id));
    if (recordCandidate) {
      events.push({
        kind: "record_candidate_preview",
        payload: recordCandidate
      });
    }
    if (!text.trim() && events.length === 0) return [];
    const deliveryMetadata = parseAssistantResponseDeliveryMetadata(message.deliveryMetadata);
    return [{
      id: message.id,
      role: message.role as "user" | "assistant",
      text,
      events,
      streaming: false,
      ...(deliveryMetadata ? {
        responseId: message.id,
        deliveryMetadata,
        ...(feedbackByResponse.has(message.id) ? {
          feedbackRating: feedbackByResponse.get(message.id)
        } : {})
      } : {})
    }];
  });
  for (const proposal of loaded.proposals) {
    if (proposal.messageId) continue;
    hydrated.push(buildOrphanProposalMessage({
      ...proposal,
      kind: proposal.kind as ProposalPayload["kind"]
    }));
  }
  return hydrated;
}

// ─── Component ──────────────────────────────────────────

export function AssistantSidebar({
  storyId,
  chapterId,
  assistantExperience: initialAssistantExperience,
  assistantFlareAvailable
}: AssistantSidebarProps) {
  const store = useAssistantChatStore();
  const backend = useEditorBackend();
  const router = useRouter();
  const convo = store.getConvo(storyId, chapterId);
  const {
    messages,
    input,
    streaming
  } = convo;
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<AssistantComposerHandle>(null);
  // Queued message: stored in a ref (no re-render needed for the value itself)
  // and mirrored into state only for the visible pill.
  const queuedRef = useRef<{
    text: string;
    chapterId: string;
  } | null>(null);
  const [queued, setQueued] = useState<string | null>(null);
  const mountedRef = useRef(true);
  // Set when the current turn's stream reports a "deadline" warning; read
  // (and reset) in executeSend's finally to decide whether to auto-continue.
  const deadlineHitRef = useRef(false);
  // Consecutive auto-continues fired without a real user message in between.
  const autoContinueCountRef = useRef(0);
  const historyRequestRef = useRef(0);
  const [conversations, setConversations] = useState<AssistantConversationSummary[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [assistantExperience, setAssistantExperience] = useState<AssistantExperience | undefined>(initialAssistantExperience);
  const assistantExperienceRef = useRef<AssistantExperience | undefined>(initialAssistantExperience);
  const responseMetadataRef = useRef(new Map<string, {
    responseId: string;
    experience: AssistantExperience;
    responseKind: "legacy_response" | "navigation" | "final_response";
  }>());
  const deliveredResponseIdsRef = useRef(new Set<string>());
  const legacyMutationActionsEnabled = initialAssistantExperience !== "v2" && assistantExperience !== "v2";
  useEffect(() => {
    assistantExperienceRef.current = initialAssistantExperience;
    setAssistantExperience(initialAssistantExperience);
  }, [initialAssistantExperience]);
  useEffect(() => {
    historyRequestRef.current += 1;
    setConversations([]);
    setIsHistoryLoading(false);
    setIsHistoryOpen(false);
    setLoadingConversationId(null);
    setHistoryError(null);
    return () => {
      historyRequestRef.current += 1;
    };
  }, [storyId]);
  useEffect(() => {
    if (backend.isDemo || convo.conversationId || convo.messages.length > 0) return;
    let cancelled = false;
    void (async () => {
      const conversations = await listConversations(storyId);
      if (!cancelled) setConversations(conversations);
      const latest = conversations[0];
      if (!latest || cancelled) return;
      const loaded = await getConversation(latest.id);
      if ("error" in loaded || cancelled) return;
      const hydrated = buildHydratedMessages(loaded);
      const current = useAssistantChatStore.getState().getConvo(storyId, chapterId);
      if (!canApplyAssistantHydration(current)) return;
      store.hydrateConversation(storyId, chapterId, loaded.conversation.id, hydrated, {
        preserveInput: true
      });
    })().catch(error => {
      console.error("[assistant] conversation hydration failed:", error);
    });
    return () => {
      cancelled = true;
    };
    // Hydrate once per editor scope; store is intentionally read imperatively.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backend.isDemo, storyId]);
  async function refreshHistory() {
    if (backend.isDemo) return;
    const requestId = ++historyRequestRef.current;
    setIsHistoryLoading(true);
    setHistoryError(null);
    try {
      const loadedConversations = await listConversations(storyId);
      if (requestId !== historyRequestRef.current) return;
      setConversations(loadedConversations);
    } catch (error) {
      if (requestId !== historyRequestRef.current) return;
      setHistoryError(error instanceof Error ? error.message : "Could not load past sessions");
    } finally {
      if (requestId === historyRequestRef.current) {
        setIsHistoryLoading(false);
      }
    }
  }
  async function openConversation(conversationId: string) {
    if (streaming || conversationId === convo.conversationId) return;
    const requestId = ++historyRequestRef.current;
    setLoadingConversationId(conversationId);
    setHistoryError(null);
    try {
      const loaded = await getConversation(conversationId);
      if (requestId !== historyRequestRef.current) return;
      if ("error" in loaded) {
        setHistoryError(loaded.error ?? "Could not open this session");
        return;
      }
      if (loaded.conversation.storyId !== storyId) {
        setHistoryError("Conversation does not belong to this story");
        return;
      }
      store.hydrateConversation(storyId, chapterId, loaded.conversation.id, buildHydratedMessages(loaded));
      queuedRef.current = null;
      setQueued(null);
      setIsHistoryOpen(false);
    } catch (error) {
      if (requestId !== historyRequestRef.current) return;
      setHistoryError(error instanceof Error ? error.message : "Could not open this session");
    } finally {
      if (requestId === historyRequestRef.current) {
        setLoadingConversationId(null);
      }
    }
  }
  function startNewSession() {
    if (streaming) return;
    historyRequestRef.current += 1;
    queuedRef.current = null;
    setQueued(null);
    setLoadingConversationId(null);
    setIsHistoryOpen(false);
    setHistoryError(null);
    autoContinueCountRef.current = 0;
    store.resetConversation(storyId, chapterId);
    inputRef.current?.focus();
  }
  function scanSavedChapter() {
    if (streaming || loadingConversationId !== null || backend.isDemo || assistantExperienceRef.current !== "v2" || !chapterId) {
      return;
    }
    void executeSend(SCAN_SAVED_CHAPTER_PROMPT, {
      chapterId,
      requestKind: "scan_saved_chapter"
    });
  }

  // Auto-scroll to bottom as new content arrives
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth"
    });
  }, [messages]);

  // Sheet visibility is presentation-only. Let an already-bounded response
  // finish into the shared, story/chapter-scoped conversation store.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Called by the user (Enter / send button). If a stream is already running,
  // capture as a queued message instead of firing immediately.
  function sendMessage() {
    if (loadingConversationId) return;
    if (!input.trim()) return;
    const submitted = input;
    store.setInput(storyId, chapterId, "");
    inputRef.current?.focus();
    if (streaming) {
      queuedRef.current = {
        text: submitted,
        chapterId
      };
      setQueued(submitted);
      return;
    }
    void executeSend(submitted);
  }

  // Fires the actual SSE request. Called directly for the first message and
  // recursively (from finally) when a queued message is waiting, or when
  // auto-continuing after a "deadline" warning (isAutoContinue: true).
  async function executeSend(text: string, opts?: {
    isAutoContinue?: boolean;
    chapterId?: string;
    requestKind?: "chat" | "scan_saved_chapter";
  }) {
    const requestChapterId = opts?.chapterId ?? chapterId;
    autoContinueCountRef.current = opts?.isAutoContinue ? autoContinueCountRef.current + 1 : 0;
    deadlineHitRef.current = false;
    store.setStreaming(storyId, requestChapterId, true);

    // The assistant reads the saved chapter from Postgres. If the author
    // just clicked Save, do not race that write with the context query.
    if (!backend.isDemo) await waitForPendingEditorSave();
    const hasUnsavedChapterChanges = !backend.isDemo && useEditorStore.getState().isDirty;

    // Capture prior turns BEFORE appending the new ones — the demo route is
    // stateless and needs the history in the request body.
    const priorMessages = useAssistantChatStore.getState().getConvo(storyId, requestChapterId).messages.filter(m => !m.isSummary && m.text.trim()).map(m => ({
      role: m.role,
      text: m.text
    }));
    const requestId = globalThis.crypto.randomUUID();
    const userMsg: Msg = {
      id: `u-${requestId}`,
      role: "user",
      text,
      events: [],
      streaming: false
    };
    const assistantId = `a-${Date.now()}`;
    const assistantPlaceholder: Msg = {
      id: assistantId,
      role: "assistant",
      text: "",
      events: [],
      streaming: true
    };
    store.appendMessage(storyId, requestChapterId, userMsg);
    store.appendMessage(storyId, requestChapterId, assistantPlaceholder);
    const currentConvId = useAssistantChatStore.getState().getConvo(storyId, requestChapterId).conversationId;
    const controller = new AbortController();
    const tracking = backend.isDemo ? null : startAuthorToolTracking({
      toolKey: "ai_assistant",
      surface: "assistant_sidebar",
      storyId,
      chapterId: requestChapterId
    });
    try {
      // In demo mode the route is stateless: send the conversation
      // history + a snapshot of the story so it has context to work with.
      const demoFields = backend.isDemo ? {
        history: priorMessages,
        demoContext: backend.getDemoAssistantContext?.(),
        turnstileToken: backend.getDemoAuthToken?.()
      } : {};
      const res = await fetch(backend.assistantStreamEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          requestId,
          mode: "assistant",
          storyId,
          chapterId: requestChapterId,
          conversationId: currentConvId,
          message: text,
          requestKind: opts?.requestKind ?? "chat",
          ...(!backend.isDemo ? {
            hasUnsavedChapterChanges
          } : {}),
          ...demoFields
        }),
        signal: controller.signal
      });
      if (!res.ok || !res.body) {
        tracking?.failed(`http_${res.status}`);
        const errText = await res.text().catch(() => res.statusText);
        appendEvent(assistantId, {
          kind: "error",
          message: `${res.status}: ${errText}`
        }, requestChapterId);
        return;
      }

      // A 2xx here means the demo prompt cleared the abuse/bot gate and is
      // streaming — the moment of real demo engagement. Fire the Lead /
      // demo_engaged conversion (deduped once per session inside the
      // helper). Demo only: the real editor is behind auth and already
      // past the funnel this campaign measures.
      if (backend.isDemo) fireDemoEngagementOnce();
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const {
          done,
          value
        } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, {
          stream: true
        });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          await handleFrame(frame, assistantId, requestChapterId);
        }
      }
      const completedMessage = useAssistantChatStore.getState().getConvo(storyId, requestChapterId).messages.find(message => message.id === assistantId);
      if (completedMessage?.events.some(event => event.kind === "error")) {
        tracking?.failed("stream_error");
      } else {
        tracking?.completed("response_streamed");
      }
    } catch (err) {
      // Cancellation is an expected transport outcome and should not be
      // rendered as an assistant error.
      if (err instanceof DOMException && err.name === "AbortError") {
        tracking?.failed("request_aborted");
        return;
      }
      tracking?.failed("network_error");
      appendEvent(assistantId, {
        kind: "error",
        message: err instanceof Error ? err.message : String(err)
      }, requestChapterId);
    } finally {
      store.setStreaming(storyId, requestChapterId, false);
      store.updateMessage(storyId, requestChapterId, assistantId, m => ({
        ...m,
        streaming: false
      }));

      // If a message was queued while we were streaming, fire it now —
      // a real user message takes priority over auto-continuing.
      const next = queuedRef.current;
      if (next) {
        queuedRef.current = null;
        if (mountedRef.current) setQueued(null);
        void executeSend(next.text, {
          chapterId: next.chapterId
        });
      } else if (deadlineHitRef.current && autoContinueCountRef.current < MAX_AUTO_CONTINUES) {
        void executeSend(AUTO_CONTINUE_MESSAGE, {
          isAutoContinue: true,
          chapterId: requestChapterId
        });
      } else {
        if (deadlineHitRef.current) {
          appendEvent(assistantId, {
            kind: "warning",
            message: "Stopped auto-continuing after several long turns — send another message to keep going."
          }, requestChapterId);
        }
        inputRef.current?.focus();
      }

      // Background summarization when history grows large. Skipped in the
      // demo — it would call a DB-backed Server Action, and the 40-prompt
      // cap keeps demo conversations short anyway.
      const currentSession = useAssistantChatStore.getState().getConvo(storyId, requestChapterId);
      const currentMessages = currentSession.messages;
      if (!backend.isDemo && assistantExperienceRef.current !== "v2" && currentMessages.length > MSG_CAP) {
        const sourceConversationId = currentSession.conversationId;
        const sourceSessionEpoch = currentSession.sessionEpoch;
        const sourceMessageRevision = currentSession.messageRevision;
        const toSummarize = currentMessages.filter(m => !m.isSummary).slice(0, MSG_SUMMARIZE_COUNT).map(m => ({
          role: m.role,
          text: m.text
        }));
        summarizeConversationHistory(toSummarize).then(result => {
          if ("summary" in result && typeof result.summary === "string") {
            const latestStore = useAssistantChatStore.getState();
            const latestSession = latestStore.getConvo(storyId, requestChapterId);
            if (latestSession.conversationId !== sourceConversationId || latestSession.sessionEpoch !== sourceSessionEpoch) return;
            latestStore.replaceWithSummary(storyId, requestChapterId, result.summary, MSG_SUMMARIZE_COUNT, sourceMessageRevision);
          } else if ("error" in result) {
            console.warn("[assistant] summarization failed:", result.error);
          }
        }).catch(err => {
          console.warn("[assistant] summarization threw:", err);
        });
      }
    }
  }
  async function handleFrame(frame: string, msgId: string, requestChapterId: string) {
    const lines = frame.split("\n");
    let event = "message";
    let dataLine = "";
    for (const line of lines) {
      if (line.startsWith("event:")) event = line.slice(6).trim();else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
    }
    if (!dataLine) return;
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(dataLine);
    } catch {
      return;
    }
    switch (event) {
      case "start":
        if (typeof data.conversationId === "string") {
          store.setConversationId(storyId, requestChapterId, data.conversationId);
        }
        if (data.experience === "legacy" || data.experience === "v2") {
          assistantExperienceRef.current = data.experience;
          setAssistantExperience(data.experience);
        }
        if (typeof data.messageId === "string" && (data.experience === "legacy" || data.experience === "v2") && (data.responseKind === "legacy_response" || data.responseKind === "navigation" || data.responseKind === "final_response")) {
          responseMetadataRef.current.set(msgId, {
            responseId: data.messageId,
            experience: data.experience,
            responseKind: data.responseKind
          });
        }
        break;
      case "text":
        if (typeof data.delta === "string") {
          appendText(msgId, data.delta, requestChapterId);
        }
        break;
      case "tool_use_start":
        appendEvent(msgId, {
          kind: "tool_use_start",
          name: String(data.name ?? "?"),
          id: String(data.id ?? "?")
        }, requestChapterId);
        break;
      case "created":
        if (backend.isDemo && backend.applyDemoCreatedRecord) {
          backend.applyDemoCreatedRecord(data as unknown as CreatedPayload);
        }
        appendEvent(msgId, {
          kind: "created",
          payload: data as unknown as CreatedPayload,
          state: "live"
        }, requestChapterId);
        break;
      case "proposal":
        appendEvent(msgId, {
          kind: "proposal",
          payload: data as unknown as ProposalPayload,
          state: "pending"
        }, requestChapterId);
        break;
      case "record_candidate_preview":
        {
          const preview = parseRecordCandidatePreviewPayload(data);
          if (!preview) {
            appendEvent(msgId, {
              kind: "error",
              message: "The record preview could not be displayed safely."
            }, requestChapterId);
            break;
          }
          appendEvent(msgId, {
            kind: "record_candidate_preview",
            payload: preview
          }, requestChapterId);
          break;
        }
      case "assistant_index_job":
        receiveAssistantIndexJobEvent(data);
        break;
      case "navigation":
        {
          const locations = Array.isArray(data.locations) ? data.locations.filter((location): location is {
            id: string;
            label: string;
            href: string;
          } => !!location && typeof location === "object" && typeof (location as Record<string, unknown>).id === "string" && typeof (location as Record<string, unknown>).label === "string" && typeof (location as Record<string, unknown>).href === "string" && /^\/[^/]/u.test((location as Record<string, unknown>).href as string)) : [];
          await applyAssistantV2ClientEvent({
            type: "navigation",
            text: typeof data.text === "string" ? data.text : "Open that Iloris page.",
            locations
          }, {
            appendFinalText: text => appendText(msgId, text, requestChapterId),
            appendError: message => appendEvent(msgId, {
              kind: "error",
              message
            }, requestChapterId),
            navigate: async href => {
              if (await prepareEditorNavigation()) router.push(href);
            }
          });
          break;
        }
      case "warning":
        if (data.reason === "deadline") {
          deadlineHitRef.current = true;
        }
        appendEvent(msgId, {
          kind: "warning",
          message: String(data.message ?? "warning")
        }, requestChapterId);
        break;
      case "error":
        appendEvent(msgId, {
          kind: "error",
          message: String(data.message ?? "error")
        }, requestChapterId);
        break;
      case "done":
        {
          const metadata = responseMetadataRef.current.get(msgId);
          const deliveryMetadata = parseAssistantResponseDeliveryMetadata(data.deliveryMetadata);
          if (metadata && deliveryMetadata) {
            const deliveredResponseId = typeof data.messageId === "string" ? data.messageId : metadata.responseId;
            store.updateMessage(storyId, requestChapterId, msgId, message => ({
              ...message,
              responseId: deliveredResponseId,
              deliveryMetadata
            }));
          }
          const deliveredResponseId = typeof data.messageId === "string" ? data.messageId : metadata?.responseId;
          if (metadata && deliveryMetadata && deliveredResponseId && !deliveredResponseIdsRef.current.has(deliveredResponseId)) {
            deliveredResponseIdsRef.current.add(deliveredResponseId);
            captureClientEvent("assistant_response_delivered", {
              response_id: deliveredResponseId,
              experience: deliveryMetadata.experience,
              response_kind: deliveryMetadata.responseKind,
              task: deliveryMetadata.task,
              graph_version: deliveryMetadata.graphVersion,
              router_prompt_version: deliveryMetadata.routerPromptVersion,
              response_prompt_version: deliveryMetadata.responsePromptVersion,
              verifier_prompt_version: deliveryMetadata.verifierPromptVersion,
              router_model: deliveryMetadata.routerModel,
              response_model: deliveryMetadata.responseModel,
              verifier_model: deliveryMetadata.verifierModel,
              tool_schema_version: deliveryMetadata.toolSchemaVersion,
              craft_rubric_version: deliveryMetadata.craftRubricVersion,
              retrieval_index_version: deliveryMetadata.retrievalIndexVersion,
              trace_version: deliveryMetadata.traceVersion
            });
          }
          responseMetadataRef.current.delete(msgId);
          break;
        }
    }
  }
  function appendText(msgId: string, delta: string, targetChapterId = chapterId) {
    store.updateMessage(storyId, targetChapterId, msgId, m => ({
      ...m,
      text: m.text + delta
    }));
  }
  function appendEvent(msgId: string, ev: ToolEvent, targetChapterId = chapterId) {
    store.updateMessage(storyId, targetChapterId, msgId, m => ({
      ...m,
      events: [...m.events, ev]
    }));
  }
  function updateEvent(msgId: string, eventIdx: number, update: (ev: ToolEvent) => ToolEvent) {
    store.updateMessage(storyId, chapterId, msgId, m => {
      const events = m.events.slice();
      if (events[eventIdx]) events[eventIdx] = update(events[eventIdx]);
      return {
        ...m,
        events
      };
    });
  }
  const bumpLoreRefresh = useEditorStore(s => s.bumpLoreRefresh);
  async function handleUndo(msgId: string, eventIdx: number) {
    if (initialAssistantExperience === "v2" || assistantExperienceRef.current === "v2") return;
    const msg = useAssistantChatStore.getState().getConvo(storyId, chapterId).messages.find(m => m.id === msgId);
    const ev = msg?.events[eventIdx];
    if (!ev || ev.kind !== "created" || ev.state !== "live") return;
    updateEvent(msgId, eventIdx, e => e.kind === "created" ? {
      ...e,
      state: "undone",
      message: "Undoing…"
    } : e);

    // Demo creations live in the in-memory Wiki and must be removed too.
    if (backend.isDemo) {
      const undone = backend.undoDemoCreatedRecord?.(ev.payload) ?? false;
      updateEvent(msgId, eventIdx, e => e.kind === "created" ? undone ? {
        ...e,
        state: "undone",
        message: "Undone"
      } : {
        ...e,
        state: "error",
        message: "Couldn't undo this creation"
      } : e);
      return;
    }
    try {
      const res = await undoAiCreation(ev.payload.recordKind, ev.payload.recordId);
      if ("error" in res) {
        updateEvent(msgId, eventIdx, e => e.kind === "created" ? {
          ...e,
          state: "error",
          message: res.error
        } : e);
        return;
      }
      updateEvent(msgId, eventIdx, e => e.kind === "created" ? {
        ...e,
        state: "undone",
        message: "Undone"
      } : e);
      bumpLoreRefresh();
    } catch (err) {
      updateEvent(msgId, eventIdx, e => e.kind === "created" ? {
        ...e,
        state: "error",
        message: err instanceof Error ? err.message : String(err)
      } : e);
    }
  }
  async function handleApprove(msgId: string, eventIdx: number) {
    if (initialAssistantExperience === "v2" || assistantExperienceRef.current === "v2") return;
    const msg = useAssistantChatStore.getState().getConvo(storyId, chapterId).messages.find(m => m.id === msgId);
    const ev = msg?.events[eventIdx];
    if (!ev || ev.kind !== "proposal" || ev.state !== "pending") return;

    // Demo never emits real proposals, but guard defensively: approve locally.
    if (backend.isDemo) {
      updateEvent(msgId, eventIdx, e => e.kind === "proposal" ? {
        ...e,
        state: "approved",
        message: "Applied"
      } : e);
      return;
    }
    updateEvent(msgId, eventIdx, e => e.kind === "proposal" ? {
      ...e,
      state: "approved",
      message: "Applying…"
    } : e);
    try {
      const res = await approveProposal(ev.payload.proposalId);
      if ("error" in res) {
        updateEvent(msgId, eventIdx, e => e.kind === "proposal" ? {
          ...e,
          state: "error",
          message: res.error
        } : e);
        return;
      }
      updateEvent(msgId, eventIdx, e => e.kind === "proposal" ? {
        ...e,
        state: "approved",
        message: "Approved"
      } : e);
      bumpLoreRefresh();
    } catch (err) {
      updateEvent(msgId, eventIdx, e => e.kind === "proposal" ? {
        ...e,
        state: "error",
        message: err instanceof Error ? err.message : String(err)
      } : e);
    }
  }
  async function handleReject(msgId: string, eventIdx: number) {
    if (initialAssistantExperience === "v2" || assistantExperienceRef.current === "v2") return;
    const msg = useAssistantChatStore.getState().getConvo(storyId, chapterId).messages.find(m => m.id === msgId);
    const ev = msg?.events[eventIdx];
    if (!ev || ev.kind !== "proposal" || ev.state !== "pending") return;
    if (backend.isDemo) {
      updateEvent(msgId, eventIdx, e => e.kind === "proposal" ? {
        ...e,
        state: "rejected",
        message: "Rejected"
      } : e);
      return;
    }
    try {
      const result = await rejectProposal(ev.payload.proposalId);
      updateEvent(msgId, eventIdx, e => e.kind === "proposal" ? "error" in result ? {
        ...e,
        state: "error",
        message: result.error
      } : {
        ...e,
        state: "rejected",
        message: "Rejected"
      } : e);
    } catch (error) {
      updateEvent(msgId, eventIdx, e => e.kind === "proposal" ? {
        ...e,
        state: "error",
        message: error instanceof Error ? error.message : String(error)
      } : e);
    }
  }
  return <div className="assistant-sidebar-content">
            {assistantExperience && !backend.isDemo && <AssistantFlareControl experience={assistantExperience} isAvailable={assistantFlareAvailable ?? initialAssistantExperience === "v2"} isDisabled={streaming} onExperienceChange={experience => {
      assistantExperienceRef.current = experience;
      setAssistantExperience(experience);
    }} />}
            <AssistantSessionToolbar voiceTools={!backend.isDemo && <AssistantVoiceTools disabled={loadingConversationId !== null} focusInput={() => inputRef.current?.focusForDictation() ?? null} />} conversations={conversations} activeConversationId={convo.conversationId} isDemo={backend.isDemo} isStreaming={streaming} isHistoryLoading={isHistoryLoading} isHistoryOpen={isHistoryOpen} canScanSavedChapter={assistantExperience === "v2" && !backend.isDemo && chapterId.length > 0} loadingConversationId={loadingConversationId} historyError={historyError} onStartNew={startNewSession} onRefreshHistory={refreshHistory} onHistoryOpenChange={setIsHistoryOpen} onOpenConversation={openConversation} onScanSavedChapter={scanSavedChapter} />
            <div ref={scrollRef} className="assistant-message-list">
                {messages.length === 0 ? <div className="assistant-empty">
                        <Sparkles className="h-8 w-8 text-primary/40 mb-2" />
                        <p className="text-xs text-muted-foreground">
                            Ask anything about your story.
                        </p>
                        {assistantExperience === "v2" ? <div className="assistant-suggestions" aria-label="Suggested prompts">
                                {ASSISTANT_V2_SUGGESTIONS.map(suggestion => <button key={suggestion} type="button" onClick={() => {
            store.setInput(storyId, chapterId, suggestion);
            inputRef.current?.focus();
          }}>
                                        {suggestion}
                                    </button>)}
                            </div> : <p className="text-[11px] text-muted-foreground/70 mt-2 leading-relaxed">
                                Try: <em>&quot;does this fire upgrade make sense?&quot;</em>,{" "}
                                <em>&quot;find scenes where Tommy uses fire&quot;</em>, or{" "}
                                <em>&quot;add a fire-tier-2 trait to Tommy&quot;</em>.
                            </p>}
                    </div> : messages.map(m => m.isSummary ? <SummaryDivider key={m.id} text={m.text} /> : <MessageBubble key={m.id} m={m} isAssistantV2={assistantExperience === "v2"} legacyMutationActionsEnabled={legacyMutationActionsEnabled} onUndo={idx => handleUndo(m.id, idx)} onApprove={idx => handleApprove(m.id, idx)} onReject={idx => handleReject(m.id, idx)} onFeedbackSaved={rating => store.updateMessage(storyId, chapterId, m.id, message => ({
        ...message,
        feedbackRating: rating
      }))} />)}
            </div>

            {queued && <div className="assistant-queued-pill">
                    <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                    <span className="assistant-queued-text">
                        Queued: {queued.length > 60 ? queued.slice(0, 60) + "…" : queued}
                    </span>
                    <button type="button" onClick={() => {
        queuedRef.current = null;
        setQueued(null);
      }} className="assistant-queued-cancel" aria-label="Cancel queued message">
                        <X className="h-3 w-3" />
                    </button>
                </div>}
            <AssistantComposer ref={inputRef} value={input} onChange={value => store.setInput(storyId, chapterId, value)} onSend={sendMessage} isStreaming={streaming} isDisabled={loadingConversationId !== null} disabledLabel="Loading session" showStreamingIndicator={streaming && !queued} isDictationUnavailable={backend.isDemo || loadingConversationId !== null} maxCharacters={assistantExperience === "v2" ? ASSISTANT_V2_MAX_MESSAGE_CHARS : undefined} />
        </div>;
}

// ─── SummaryDivider ─────────────────────────────────────

function SummaryDivider(t0) {
  const $ = _c(3);
  const {
    text
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="assistant-summary-label">Earlier conversation</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== text) {
    t2 = <div className="assistant-summary-divider">{t1}<p className="assistant-summary-text">{text}</p></div>;
    $[1] = text;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// ─── MessageBubble ──────────────────────────────────────

function MessageBubble(t0) {
  const $ = _c(39);
  const {
    m,
    isAssistantV2,
    legacyMutationActionsEnabled,
    onUndo,
    onApprove,
    onReject,
    onFeedbackSaved
  } = t0;
  const isUser = m.role === "user";
  const t1 = `assistant-msg ${isUser ? "assistant-msg--user" : "assistant-msg--assistant"}`;
  let t2;
  if ($[0] !== isUser) {
    t2 = !isUser && <span>assistant</span>;
    $[0] = isUser;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isAssistantV2 || $[3] !== isUser || $[4] !== m.streaming || $[5] !== m.text) {
    t3 = !isUser && m.streaming && <span className="assistant-streaming" role="status" aria-live="polite" data-testid={isAssistantV2 ? "assistant-v2-progress-status" : undefined}><span className="assistant-pulse" />{isAssistantV2 ? m.text.length > 0 ? " responding\u2026" : " working\u2026" : " streaming\u2026"}</span>;
    $[2] = isAssistantV2;
    $[3] = isUser;
    $[4] = m.streaming;
    $[5] = m.text;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isUser || $[8] !== m.streaming || $[9] !== m.text) {
    t4 = !isUser && !m.streaming && m.text.length > 0 && <span className="assistant-done">✓</span>;
    $[7] = isUser;
    $[8] = m.streaming;
    $[9] = m.text;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <div className="assistant-msg-meta">{t2}{t3}{t4}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== legacyMutationActionsEnabled || $[16] !== m.events || $[17] !== onApprove || $[18] !== onReject || $[19] !== onUndo) {
    t6 = m.events.length > 0 && <div className="assistant-chips">{m.events.map((ev, i) => <EventChip key={i} ev={ev} legacyMutationActionsEnabled={legacyMutationActionsEnabled} onUndo={() => onUndo(i)} onApprove={() => onApprove(i)} onReject={() => onReject(i)} />)}</div>;
    $[15] = legacyMutationActionsEnabled;
    $[16] = m.events;
    $[17] = onApprove;
    $[18] = onReject;
    $[19] = onUndo;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== isUser || $[22] !== m.deliveryMetadata?.task || $[23] !== m.streaming || $[24] !== m.text) {
    t7 = m.text ? isUser ? m.text : <AssistantMessageContent text={m.text} isCoaching={m.deliveryMetadata?.task === "coach"} /> : m.streaming ? <span className="opacity-50">…</span> : null;
    $[21] = isUser;
    $[22] = m.deliveryMetadata?.task;
    $[23] = m.streaming;
    $[24] = m.text;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== isUser || $[27] !== m.deliveryMetadata || $[28] !== m.feedbackRating || $[29] !== m.responseId || $[30] !== m.streaming || $[31] !== onFeedbackSaved) {
    t8 = !isUser && !m.streaming && m.responseId && m.deliveryMetadata && m.deliveryMetadata.responseKind !== "navigation" && <AssistantResponseFeedbackControls responseId={m.responseId} deliveryMetadata={m.deliveryMetadata} initialRating={m.feedbackRating} onSaved={onFeedbackSaved} />;
    $[26] = isUser;
    $[27] = m.deliveryMetadata;
    $[28] = m.feedbackRating;
    $[29] = m.responseId;
    $[30] = m.streaming;
    $[31] = onFeedbackSaved;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== t1 || $[34] !== t5 || $[35] !== t6 || $[36] !== t7 || $[37] !== t8) {
    t9 = <div className={t1}>{t5}{t6}{t7}{t8}</div>;
    $[33] = t1;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  return t9;
}

// ─── EventChip ──────────────────────────────────────────

function EventChip(t0) {
  const $ = _c(18);
  const {
    ev,
    legacyMutationActionsEnabled,
    onUndo,
    onApprove,
    onReject
  } = t0;
  if (ev.kind === "tool_use_start") {
    let t1;
    if ($[0] !== ev.id || $[1] !== ev.name) {
      t1 = <span className="assistant-chip assistant-chip--tool" title={ev.id}>🔧 {ev.name}</span>;
      $[0] = ev.id;
      $[1] = ev.name;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (ev.kind === "created") {
    let t1;
    if ($[3] !== ev || $[4] !== legacyMutationActionsEnabled || $[5] !== onUndo) {
      t1 = <CreatedCard ev={ev} legacyMutationActionsEnabled={legacyMutationActionsEnabled} onUndo={onUndo} />;
      $[3] = ev;
      $[4] = legacyMutationActionsEnabled;
      $[5] = onUndo;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  if (ev.kind === "proposal") {
    let t1;
    if ($[7] !== ev || $[8] !== legacyMutationActionsEnabled || $[9] !== onApprove || $[10] !== onReject) {
      t1 = <ProposalCard ev={ev} legacyMutationActionsEnabled={legacyMutationActionsEnabled} onApprove={onApprove} onReject={onReject} />;
      $[7] = ev;
      $[8] = legacyMutationActionsEnabled;
      $[9] = onApprove;
      $[10] = onReject;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    return t1;
  }
  if (ev.kind === "record_candidate_preview") {
    let t1;
    if ($[12] !== ev) {
      t1 = <RecordCandidatePreviewCard ev={ev} />;
      $[12] = ev;
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    return t1;
  }
  if (ev.kind === "warning") {
    let t1;
    if ($[14] !== ev.message) {
      t1 = <span className="assistant-chip assistant-chip--warning">⚠ {ev.message}</span>;
      $[14] = ev.message;
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    return t1;
  }
  let t1;
  if ($[16] !== ev.message) {
    t1 = <span className="assistant-chip assistant-chip--error">✗ {ev.message}</span>;
    $[16] = ev.message;
    $[17] = t1;
  } else {
    t1 = $[17];
  }
  return t1;
}
function recordCandidateOperationLabel(operation: Extract<ToolEvent, {
  kind: "record_candidate_preview";
}>["payload"]["operations"][number]): string {
  const subject = operation.targetLabel ?? operation.payload.name ?? operation.payload.title;
  const label = subject ? `: ${subject}` : "";
  switch (operation.kind) {
    case "wiki_entry_create":
      return `Create Wiki entry${label}`;
    case "wiki_entry_update":
      return `Update Wiki entry${label}`;
    case "wiki_connection_create":
      return `Create Wiki connection${operation.payload.relationLabel ? `: ${operation.payload.relationLabel}` : ""}`;
    case "plot_thread_create":
      return `Create plot thread${label}`;
    case "plot_thread_update":
      return `Update plot thread${label}`;
    case "plot_event_create":
      return `Create plot event${label}`;
    case "plot_event_update":
      return `Update plot event${label}`;
  }
}
function recordCandidateFieldLabel(field: string): string {
  switch (field) {
    case "relationLabel":
      return "Relationship";
    case "category":
      return "Category";
    case "name":
      return "Name";
    case "title":
      return "Title";
    case "label":
      return "Label";
    case "description":
      return "Description";
    case "body":
      return "Details";
    default:
      return field;
  }
}
const RECORD_CANDIDATE_FIELD_ORDER = ["category", "name", "title", "label", "description", "body", "relationLabel"] as const;
function visibleRecordCandidateFields(payload: Readonly<Record<string, string | null>>): Array<[string, string]> {
  return Object.entries(payload).filter((entry): entry is [string, string] => typeof entry[1] === "string").sort(([left], [right]) => {
    const leftIndex = RECORD_CANDIDATE_FIELD_ORDER.indexOf(left as (typeof RECORD_CANDIDATE_FIELD_ORDER)[number]);
    const rightIndex = RECORD_CANDIDATE_FIELD_ORDER.indexOf(right as (typeof RECORD_CANDIDATE_FIELD_ORDER)[number]);
    return (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex) - (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex) || left.localeCompare(right);
  });
}
function RecordCandidatePreviewCard(t0) {
  const $ = _c(11);
  const {
    ev
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="assistant-record-preview-title">{"Wiki & plot preview"}</div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== ev.payload.operations) {
    let t3;
    if ($[3] !== ev.payload.operations.length) {
      t3 = operation => <li key={operation.position}><details open={ev.payload.operations.length === 1}><summary>{recordCandidateOperationLabel(operation)}{operation.verificationStatus !== "verified" && <span className="assistant-record-preview-status">{operation.verificationStatus}</span>}</summary>{operation.kind.endsWith("_update") && <p className="assistant-record-preview-change-heading">Proposed field changes</p>}<dl className="assistant-record-preview-fields">{visibleRecordCandidateFields(operation.payload).map(t4 => {
              const [field, value] = t4;
              return <div key={field}><dt>{recordCandidateFieldLabel(field)}</dt>{operation.kind.endsWith("_update") ? operation.previousPayload ? <dd className="assistant-record-preview-diff"><span><strong>Before</strong>{operation.previousPayload[field] || "Not set"}</span><span><strong>After</strong>{value}</span></dd> : <dd>{value}</dd> : <dd>{value}</dd>}</div>;
            })}</dl></details></li>;
      $[3] = ev.payload.operations.length;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = ev.payload.operations.map(t3);
    $[1] = ev.payload.operations;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <ol className="assistant-record-preview-list">{t2}</ol>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="assistant-card-status">No story records changed.</p>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="assistant-card-actions"><button type="button" className="assistant-card-btn" aria-label="Apply" disabled={true}>Apply</button><span className="assistant-card-status">Preview only in this release</span></div>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3) {
    t6 = <div className="assistant-card assistant-card--record-preview">{t1}{t3}{t4}{t5}</div>;
    $[9] = t3;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

// ─── CreatedCard (apply-with-undo) ──────────────────────

function CreatedCard({
  ev,
  legacyMutationActionsEnabled,
  onUndo
}: {
  ev: Extract<ToolEvent, {
    kind: "created";
  }>;
  legacyMutationActionsEnabled: boolean;
  onUndo: () => void;
}) {
  const aiCreatedMs = new Date(ev.payload.aiCreatedAt).getTime();
  const [remainingMs, setRemainingMs] = useState(() => Math.max(0, UNDO_WINDOW_MS - (Date.now() - aiCreatedMs)));
  useEffect(() => {
    if (ev.state !== "live") return;
    if (remainingMs <= 0) return;
    const id = window.setInterval(() => {
      const left = Math.max(0, UNDO_WINDOW_MS - (Date.now() - aiCreatedMs));
      setRemainingMs(left);
      if (left <= 0) clearInterval(id);
    }, 250);
    return () => clearInterval(id);
    // remainingMs intentionally omitted — including it restarts the interval
    // ~4×/sec, defeating the point of running a single timer per card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ev.state, aiCreatedMs]);
  const undoExpired = remainingMs <= 0;
  const canUndo = ev.state === "live" && !undoExpired;
  const seconds = Math.ceil(remainingMs / 1000);
  return <div className="assistant-card assistant-card--created">
            <div className="assistant-card-line">
                <span className="text-xs">✓ {ev.payload.display}</span>
            </div>
            <div className="assistant-card-actions">
                {ev.state === "live" && canUndo && legacyMutationActionsEnabled && <button type="button" onClick={onUndo} className="assistant-card-btn assistant-card-btn--undo" title={`Undo within ${seconds}s`}>
                        <Undo2 className="h-3 w-3" />
                        Undo ({seconds}s)
                    </button>}
                {ev.state === "live" && !legacyMutationActionsEnabled ? <span className="assistant-card-status">
                        {LEGACY_ACTION_UNAVAILABLE_MESSAGE}
                    </span> : ev.state === "live" && undoExpired && <span className="assistant-card-status">Locked in</span>}
                {ev.state === "undone" && <span className="assistant-card-status">{ev.message ?? "Undone"}</span>}
                {ev.state === "error" && <span className="assistant-card-status assistant-card-status--error">
                        ✗ {ev.message ?? "Error"}
                    </span>}
            </div>
        </div>;
}

// ─── ProposalCard (Approve / Reject) ────────────────────

function ProposalCard(t0) {
  const $ = _c(11);
  const {
    ev,
    legacyMutationActionsEnabled,
    onApprove,
    onReject
  } = t0;
  let t1;
  if ($[0] !== ev.payload.display) {
    t1 = <div className="assistant-card-line"><span className="text-xs">❓ {ev.payload.display}</span></div>;
    $[0] = ev.payload.display;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== ev.message || $[3] !== ev.state || $[4] !== legacyMutationActionsEnabled || $[5] !== onApprove || $[6] !== onReject) {
    t2 = <div className="assistant-card-actions">{ev.state === "pending" && legacyMutationActionsEnabled ? <><button type="button" onClick={onApprove} className="assistant-card-btn assistant-card-btn--approve"><Check className="h-3 w-3" />Approve</button><button type="button" onClick={onReject} className="assistant-card-btn assistant-card-btn--reject"><X className="h-3 w-3" />Reject</button></> : ev.state === "pending" ? <span className="assistant-card-status">{LEGACY_ACTION_UNAVAILABLE_MESSAGE}</span> : <span className={ev.state === "error" ? "assistant-card-status assistant-card-status--error" : "assistant-card-status"}>{ev.state === "approved" ? `✓ ${ev.message ?? "Approved"}` : ev.state === "rejected" ? "\u2717 Rejected" : `✗ ${ev.message ?? "Error"}`}</span>}</div>;
    $[2] = ev.message;
    $[3] = ev.state;
    $[4] = legacyMutationActionsEnabled;
    $[5] = onApprove;
    $[6] = onReject;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = <div className="assistant-card assistant-card--proposal">{t1}{t2}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}